/* Product Card - Component, Variants, and Color Swatch Handler */

if (!customElements.get("product-card")) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();
      this.isQuickCart = this.classList.contains(
        "product-card--quick-cart"
      );
      this.init();
    }

    init() {
      this.initForm();
      this.initVariants();
      this.imageBorderCalc();
      this.calloutContrastCalc();
      if (
        this.querySelector(".product-card__media") &&
        this.querySelector(".product-card__media").classList.contains(
          "product-card__media--hoverable"
        )
      ) {
        this.secondImgHoverStates();
      }

      // if quick-cart-drawer does not exists, remove trigger buttons
      if (!document.querySelector("quick-cart-drawer")) {
        this.querySelector(".quick-cart-drawer__trigger")?.remove();
      }
    }

    /**
     * Add to Cart Form
     */
    initForm() {
      /** Variables */
      this.cart =
        document.querySelector("cart-notification") ||
        document.querySelector("cart-drawer");
      this.addToCartForm = this.querySelector(
        "form.product-card__add-to-cart--form"
      );
      if (this.addToCartForm) {
        this.addToCartForm.removeAttribute("method");
        this.addToCartForm.removeAttribute("action");
      }
      this.submitButton = this.querySelector('button[type="submit"]');
      /** Event Listeners */
      if (this.addToCartForm) {
        this.addToCartForm.addEventListener(
          "submit",
          this.submitAddToCartForm.bind(this)
        );
      }
    }

    submitAddToCartForm(event) {
      event.preventDefault();

      this.submitButton.setAttribute("disabled", "");
      this.submitButton.classList.add("add-to-cart--is-disabled");

      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];

      const formData = new FormData(event.target);
      if (this.cart) {
        formData.append(
          "sections",
          this.cart
            .getSectionsToRender()
            .map(section => section.section)
        );
        formData.append("sections_url", window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then(response => response.json())
        .then(response => {
          if (response.errors) {
            this.handleErrorMessage(response.errors);
            return;
          }

          if (this.cart) {
            this.cart.renderContents(response);
          }

          updateCartCounters();
        })
        .catch((error) => { console.error(error) })
        .finally(() => {
          this.submitButton.removeAttribute("disabled");
          this.submitButton.classList.remove(
            "add-to-cart--is-disabled"
          );

          const quickCartDrawer = this.closest("quick-cart-drawer");
          if (quickCartDrawer) {
            quickCartDrawer.close();
          }
        });
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.querySelector(
        ".product-form__error-message-wrapper"
      );
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessageWrapper.querySelector(
        ".product-form__error-message"
      );

      this.errorMessageWrapper.toggleAttribute(
        "hidden",
        !errorMessage
      );

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }

      setTimeout(() => {
        this.resetErrorMessage();
      }, 5000);
    }

    resetErrorMessage() {
      if (!this.errorMessageWrapper) return;
      this.errorMessageWrapper.toggleAttribute("hidden", true);
      this.errorMessageWrapper.textContent = "";
    }

    /**
     * Product Variants
     */
    initVariants() {
      this.getVariantsData();

      // event listeners for variant buttons
      this.querySelectorAll(".button--variant input").forEach(
        variantButton => {
          variantButton.addEventListener(
            "click",
            this.onClickOptionRadioInputs.bind(this)
          );
        }
      );

      // set initial checked options
      let initialCheckedOptions = [];
      this.querySelectorAll(".variant-option-radio-input").forEach(
        radioInput => {
          if (radioInput.checked) {
            initialCheckedOptions.push(radioInput.value);
          }
        }
      );

      // ensure at least one option is selected on load
      if (initialCheckedOptions.length === 0) {
        this.querySelector(".variant-option-radio-input")?.click();
      }

      // immediately update variant statuses to set disabled states on load
      this.updateVariantStatuses();

      const { currentVariantId } = this.findCurrentVariantFromOptionRadioInputs();

      // update media and cart button states
      if (!this.querySelector(".product-card__media")?.dataset.updateMedia) {
        this.updateMediaForVariant(currentVariantId);
      }
      this.setCartButtonState();
    }

    updateVariantStatuses() {

      const selectedOptionOneVariants = this.variantsObj?.filter(
        variant =>
          this.querySelector(":checked")?.value === variant.option1
      );

      const inputWrappers = [
        ...this.querySelectorAll(".js-product-card-options")
      ];

      inputWrappers.forEach((option, index) => {
        if (index === 0) return;
        const optionInputs = [
          ...option.querySelectorAll('input[type="radio"]')
        ];
        const previousOptionSelected =
          inputWrappers[index - 1].querySelector(":checked")?.value;
        const availableOptionInputsValue = selectedOptionOneVariants
          ?.filter(
            variant =>
              variant.available &&
              variant[`option${index}`] === previousOptionSelected
          )
          .map(variantOption => variantOption[`option${index + 1}`]);

        this.setInputAvailability(
          optionInputs,
          availableOptionInputsValue
        );
      });
    }

    setInputAvailability(elementList, availableValuesList) {
      elementList.forEach(element => {
        const value = element.getAttribute("value");
        const availableElement = availableValuesList.includes(value);
        element
          .closest("li")
          .classList.toggle("disabled", !availableElement);
        element.toggleAttribute("disabled", !availableElement);
      });
    }

    onClickOptionRadioInputs(event) {
      const {
        currentVariantId,
        currentCheckedOptions,
        currentVariantMediaId
      } = this.findCurrentVariantFromOptionRadioInputs();

      this.clearRadioInputStates(event);

      if (currentCheckedOptions.includes(event.target.value)) {
        // console.log('event.target', event.target);
        event.target.setAttribute("checked", "");
        // event.target.closest("div").classList.add("checked");

        this.updateVariantStatuses();

        this.disableAddToCartButons();

        // call to update price when variant is changed
        this.updatePriceForVariant(currentVariantId);

        // call to update discount badge when variant is changed
        this.updateDiscountBadgeForVariant(currentVariantId);
      }

      // update form input variant-id
      this.updateFormVariantIdInput();

      // update product image shown
      let allVariantIdsWithImageAvailable = [];
      this.querySelectorAll("[data-product-images]").forEach(el => {
        el.getAttribute("data-product-images")
          .split(",")
          .forEach(v => {
            allVariantIdsWithImageAvailable.push(v);
          });
      });
      if (allVariantIdsWithImageAvailable.includes(currentVariantId.toString())) {
        this.updateMediaForVariant(currentVariantId);
      }

      if (this.isQuickCart && currentVariantMediaId) {
        document
          .querySelector("quick-cart-drawer")
          .setActiveMedia(currentVariantMediaId);
      }

      // update product link
      this.querySelectorAll("[data-product-link]").forEach(
        linkItem => {
          const currentLink = linkItem.getAttribute("href");
          if (currentVariantId) {
            const newLink =
              currentLink.split("?variant=")[0] +
              "?variant=" +
              currentVariantId;
            linkItem.setAttribute("href", newLink);
          }
        }
      );
    }

    setCartButtonState() {
      const { currentVariantAvailable } = this.findCurrentVariantFromOptionRadioInputs();
      if (!currentVariantAvailable) return;
      this.submitButton?.toggleAttribute(
        "disabled",
        !currentVariantAvailable
      );
    }

    updateMediaForVariant(currentVariantId) {
      if (!currentVariantId) return;
      this.querySelectorAll("[data-product-images]").forEach(el => {
        el.classList.toggle(
          "hidden",
          !el
            .getAttribute("data-product-images")
            .includes(currentVariantId)
        );
      });
    }

    updateFormVariantIdInput() {
      const { currentVariantId } = this.findCurrentVariantFromOptionRadioInputs();
      const input = this.querySelector('input[name="id"]');
      if (currentVariantId && input) {
        this.querySelector('input[name="id"]').value = currentVariantId;
      }
    }

    updatePriceForVariant(variantId) {
      // Assuming the variantsObj contains the variant data, including price
      const selectedVariant = this.variantsObj?.find(
        variant => variant.id === variantId
      );

      if (selectedVariant) {
        let priceContainer = this.querySelector(".price__container");
        // if priceContainer does not exist, check previous element
        if (!priceContainer) {
          const previousElement = this.previousElementSibling;
          if (previousElement) {
            // check if previous element has class of price__container
            priceContainer = previousElement.querySelector(
              ".price__container"
            );
          }
        }

        if (!priceContainer) return;
        const moneyFormat = priceContainer.dataset.moneyFormat;
        const price = formatPrice(
          moneyFormat,
          selectedVariant.price / 100
        );
        const compare_at_price = formatPrice(
          moneyFormat,
          selectedVariant.compare_at_price / 100
        );
        const labelPriceRegular =
          priceContainer.dataset.labelPriceRegular;
        const labelPriceSale = priceContainer.dataset.labelPriceSale;

        if (compare_at_price > price) {
          // generate html block and append to price container
          const priceHtml = `
            <div class="price__sale">
              <div class="price__sale-inner">
                <span class="visually-hidden">${labelPriceSale}</span>
                <s>${compare_at_price}</s>
                <ins>
                  <span class="visually-hidden">${labelPriceRegular}</span>
                  <span class="igPrice" data-variant-id="${selectedVariant.id}">${price}</span>
                </ins>
              </div>
            </div>
          `;
          priceContainer.innerHTML = priceHtml;
        } else {
          // generate html block and append to price container
          const priceHtml = `
            <div class="price__sale">
              <div class="price__sale-inner">
                <span class="visually-hidden">${labelPriceRegular}</span>
                <span class="igPrice" data-variant-id="${selectedVariant.id}">${price}</span>
              </div>
            </div>
          `;
          priceContainer.innerHTML = priceHtml;
        }
      }
    }

    updateDiscountBadgeForVariant(variantId) {
      const selectedVariant = this.variantsObj?.find(
        variant => variant.id === variantId
      );
      if (selectedVariant) {
        let discountBadge = this.querySelector(
          ".product-card__badge--discount"
        );
        // if discountBadge does not exist, check previous element
        if (!discountBadge) {
          const previousElement = this.previousElementSibling;
          if (previousElement) {
            // check if previous element has class of product-card__badge--discount
            discountBadge = previousElement.querySelector(
              ".product-card__badge--discount"
            );
          }
        }

        if (discountBadge) {
          if (
            selectedVariant.compare_at_price > selectedVariant.price
          ) {
            discountBadge.classList.remove("hidden");
            const percentageElement =
              discountBadge.querySelector(".percentage");
            // calculate discount percentage
            const discountPercentage = Math.round(
              ((selectedVariant.compare_at_price -
                selectedVariant.price) /
                selectedVariant.compare_at_price) *
                100
            );
            // update discount percentage
            percentageElement.textContent = discountPercentage;
          } else {
            discountBadge.classList.add("hidden");
          }
        }
      }
    }

    disableCheckedVariantOptions() {
      this.querySelectorAll(
        ".variant-option-radio-input[checked]"
      ).forEach(variantBtn => {
        if (this.isQuickCart) {
          variantBtn.setAttribute("disabled", "");
          // variantBtn.closest("div").classList.add("disabled");
        } else {
          variantBtn.removeAttribute("disabled");
        }
      });
    }

    disableAddToCartButons() {
      const { currentVariantAvailable } =
        this.findCurrentVariantFromOptionRadioInputs();
      const submitBtn = this.querySelector(
        ".product-card__add-to-cart--form button[type='submit']"
      );

      if (
        submitBtn &&
        !submitBtn.classList.contains(
          "product-card__add-to-cart--button"
        )
      ) {
        if (!currentVariantAvailable) {
          submitBtn.setAttribute("disabled", "");
        } else {
          submitBtn.removeAttribute("disabled");
        }
      }
    }

    clearRadioInputStates(event) {
      event.target
        .closest(".js-product-card-options")
        .querySelectorAll(".variant-option-radio-input")
        .forEach(radioInput => {
          radioInput.removeAttribute("checked");
          // radioInput.closest("div").classList.remove("checked");
        });
    }

    findCurrentVariantFromOptionRadioInputs() {
      let currentVariantAvailable = null;
      let currentVariantId = "";
      let currentVariantMediaId = "";
      let currentCheckedOptions = [];

      this.querySelectorAll(".js-product-card-options").forEach(
        item => {
          if (!item.parentNode.classList.contains("hidden")) {
            item
              .querySelectorAll('input[type="radio"]')
              .forEach(radioInput => {
                if (radioInput.checked) {
                  currentCheckedOptions.push(radioInput.value);
                }
              });
          } else {
            // hidden variant (size or etc) always uses the first option
            currentCheckedOptions.push(
              item.querySelector('input[type="radio"]').value
            );
          }
        }
      );

      this.variantsObj?.forEach(item => {
        if (
          item.options.join() === currentCheckedOptions.join() ||
          (item.options.length === 1 &&
            currentCheckedOptions.length === 0)
        ) {
          currentVariantId = item.id;
          currentVariantAvailable = item.available;
          currentVariantMediaId = item.featured_media?.id || null;
        }
      });

      return {
        currentVariantAvailable,
        currentVariantId,
        currentCheckedOptions,
        currentVariantMediaId
      };
    }

    findAndDisableAllUnavailableOptionsForSelectedVariant(
      selectedOption
    ) {
      let selectedOptionName = "";
      if (selectedOption) {
        selectedOptionName = selectedOption.value;
      } else {
        selectedOptionName = this.querySelectorAll(
          ".variant-option-radio-input"
        )[0].value;
      }

      const { currentCheckedOptions } =
        this.findCurrentVariantFromOptionRadioInputs();
      const optionIndex =
        currentCheckedOptions.indexOf(selectedOptionName) + 1;

      this.variantsObj.forEach(item => {
        if (
          item[`option${optionIndex}`] === selectedOptionName &&
          !item.available
        ) {
          [1, 2, 3].forEach(i => {
            if (i !== optionIndex && item[`option${i}`]) {
              const itemToDisable = this.querySelector(
                `.variant-option-radio-input[value="${
                  item[`option${i}`]
                }"]`
              );
              // itemToDisable.setAttribute("disabled", "");
              itemToDisable.closest("li").classList.add("disabled");
            }
          });
        }
      });
    }

    getVariantsData() {
      const variantsObj = JSON.parse(
        this.querySelector("[data-product-variants-json]")
          ? this.querySelector("[data-product-variants-json]")
              .textContent
          : "[]"
      );
      if (variantsObj && Object.keys(variantsObj).length === 0)
        return false;
      this.variantsObj = variantsObj;
      return variantsObj;
    }

    /** Additional event listerner for styling on hovering mutliple nested items */
    secondImgHoverStates() {
      const hoverableItem = this.querySelector(
        ".product-card__actions"
      );

      if (!hoverableItem || window.innerWidth < 750) {
        return;
      }

      hoverableItem.addEventListener("mouseenter", () =>
        hoverableItem.classList.add("is--hovering")
      );
      hoverableItem.addEventListener("mouseleave", () =>
        hoverableItem.classList.remove("is--hovering")
      );
    }

    imageBorderCalc() {
      const images = document.querySelectorAll(".product-swatch__inner-border--enable img");

      if(images) {
        images.forEach((image) => {
          if (image.complete) {
            processImage(image);
          } else {
            image.onload = () => processImage(image);
          }
        });
      }

      function processImage(image) {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(image, 0, 0, width, height);

        const centerX = Math.floor(width / 2);
        const centerY = Math.floor(height / 2);

        const pixelData = ctx.getImageData(centerX, centerY, 1, 1).data;
        const r = pixelData[0];
        const g = pixelData[1];
        const b = pixelData[2];

        const darkenFactor = 0.8;
        const darkenedR = Math.max(0, Math.floor(r * darkenFactor));
        const darkenedG = Math.max(0, Math.floor(g * darkenFactor));
        const darkenedB = Math.max(0, Math.floor(b * darkenFactor));
        const darkenedColor = `rgb(${darkenedR}, ${darkenedG}, ${darkenedB})`;

        image.style.borderColor = darkenedColor;
      }
    }

    /**
     * Samples the actual product image pixels behind the callout badge
     * (top-start corner, where .product-card__callout sits) and toggles
     * product-card__callout--on-dark so the badge renders solid white on a
     * dark photo and solid black otherwise -- real brightness, not a CSS
     * blend-mode approximation.
     */
    calloutContrastCalc() {
      const callout = this.querySelector(".product-card__callout");
      // No dot means product-badges rendered no text for this card (see
      // card-product.liquid) -- nothing to contrast, skip the canvas work.
      if (!callout || !callout.querySelector(".product-card__callout-dot")) {
        return;
      }
      // Merchant set product.metafields.custom.callout_color -- inline style
      // already wins the cascade, but skip the canvas sampling entirely.
      if (callout.dataset.calloutColorOverride === "true") {
        return;
      }

      const image = this.querySelector(
        ".product-card__media img:not(.hidden):not(.hover-img)"
      ) || this.querySelector(".product-card__media img");
      if (!image) {
        return;
      }

      const sample = () => {
        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;
        if (!width || !height) {
          return;
        }

        // Only the corner behind the badge, not the whole photo.
        const sampleWidth = Math.max(1, Math.floor(width * 0.25));
        const sampleHeight = Math.max(1, Math.floor(height * 0.2));
        const size = 16;

        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");

        try {
          ctx.drawImage(image, 0, 0, sampleWidth, sampleHeight, 0, 0, size, size);
          const { data } = ctx.getImageData(0, 0, size, size);

          let total = 0;
          let count = 0;
          for (let i = 0; i < data.length; i += 4) {
            total += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
            count++;
          }

          const avgLuminance = total / count;
          callout.classList.toggle("product-card__callout--on-dark", avgLuminance < 128);
        } catch (error) {
          // Canvas read blocked (e.g. tainted by a non-CORS image host) --
          // leave the default black-on-light styling rather than throwing.
          console.error(error);
        }
      };

      if (image.complete) {
        sample();
      } else {
        image.addEventListener("load", sample, { once: true });
      }
    }

  }
  customElements.define("product-card", ProductCard);

  // Intercept product-group swatch link clicks so they swap content via AJAX
// instead of causing a full page reload.
//
// product-groups.liquid renders each color as <a href="/products/..."> because
// each color is a separate Shopify product. This handler catches those clicks,
// prevents navigation, and either:
//   - on the product page: uses the product-info element's fetch+viewTransition swap
//   - on a collection card: updates the card link and highlighted swatch state
document.addEventListener('click', function(event) {
  const link = event.target.closest('.product-option__link[href]');
  if (!link) return;

  // --- Product page ---
  const productInfo = link.closest('product-info');
  if (productInfo) {
    event.preventDefault();

    const productUrl = link.getAttribute('href').split('?')[0];
    if (productUrl === productInfo.dataset.url) return;

    // Block concurrent taps — rapid tapping would otherwise keep aborting and
    // restarting the fetch, making the UI feel frozen with no feedback.
    if (productInfo.dataset.swapLoading) return;
    productInfo.dataset.swapLoading = '1';

    // Update active swatch state immediately for responsiveness
    link.closest('fieldset')?.querySelectorAll('label').forEach(l => l.classList.remove('active'));
    link.closest('label')?.classList.add('active');

    // Dim the section so the user knows something is happening
    const productGroups = link.closest('.product__product-groups');
    if (productGroups) {
      productGroups.style.opacity = '0.5';
      productGroups.style.pointerEvents = 'none';
    }

    // Safety valve: if the fetch hangs, re-enable after 6s
    const loadingGuard = setTimeout(function() {
      delete productInfo.dataset.swapLoading;
      if (productGroups) {
        productGroups.style.opacity = '';
        productGroups.style.pointerEvents = '';
      }
    }, 6000);

    productInfo.pendingRequestUrl = productUrl;
    const requestUrl = productInfo.buildRequestUrlWithParams(productUrl, [], false);
    productInfo.renderProductInfo({
      requestUrl: requestUrl,
      targetId: productInfo.id,
      callback: function(doc) {
        clearTimeout(loadingGuard);
        delete productInfo.dataset.swapLoading;
        if (productGroups) {
          productGroups.style.opacity = '';
          productGroups.style.pointerEvents = '';
        }
        const newProductInfo = doc.querySelector('product-info');
        if (!newProductInfo) return;
        const newVariant = productInfo.getSelectedVariant(newProductInfo);
        const variantParam = newVariant?.id ? '?variant=' + newVariant.id : '';
        window.history.pushState({}, '', productUrl + variantParam);

        // Run pre-process callbacks on incoming content before insertion
        productInfo.preProcessHtmlCallbacks.forEach(cb => cb(newProductInfo));

        // Strip external scripts already in the document before setInnerHTML runs
        // to prevent "already declared" errors for top-level class declarations
        // (SwiperComponent, _db, HTMLUpdateUtility, etc.) in files like
        // swiper-component.js and product-media-gallery.js.
        // Inline scripts are kept so per-instance initialization still fires.
        const loadedSrcs = new Set(
          Array.from(document.querySelectorAll('script[src]')).map(s => s.getAttribute('src'))
        );
        newProductInfo.querySelectorAll('script[src]').forEach(s => {
          if (loadedSrcs.has(s.getAttribute('src'))) s.remove();
        });

        // Build a fresh element (setInnerHTML re-executes any remaining inline scripts)
        const swapWrapper = document.createElement('div');
        HTMLUpdateUtility.setInnerHTML(swapWrapper, newProductInfo.outerHTML);
        const freshElement = swapWrapper.firstChild;

        // Lock the grid height before the swap so the page doesn't shift
        // when the new product-info element is a different height during load.
        const productGrid = productInfo.closest('.product-grid');
        const currentGridHeight = productGrid?.offsetHeight;
        if (productGrid && currentGridHeight) productGrid.style.minHeight = currentGridHeight + 'px';

        // Swap elements immediately — viewTransition leaves the old element
        // in the DOM (display:none) for 500 ms, which causes iOS Safari to
        // cache stale hit-test positions and stop registering all taps.
        productInfo.parentNode.insertBefore(freshElement, productInfo);
        productInfo.remove();

        // Run post-process callbacks on the newly inserted element
        productInfo.postProcessHtmlCallbacks.forEach(cb => cb(freshElement));

        // Force a synchronous layout pass then a paint cycle so iOS fully
        // invalidates its hit-test compositing cache for the new element.
        void document.body.offsetHeight;
        requestAnimationFrame(() => {
          // Release the height lock now that the new element is in the DOM
          if (productGrid) productGrid.style.minHeight = '';

          // On mobile, the page is typically scrolled down to the swatches when
          // the user picks a color. The DOM swap can drift scroll further down via
          // scroll anchoring. Snap back so the new product section is in view.
          if (window.innerWidth < 750) {
            freshElement.scrollIntoView({ behavior: 'instant', block: 'start' });
          }

          void freshElement.offsetHeight;
          const galleryEl = freshElement.querySelector('swiper-product-gallery');
          const thumbsEl = freshElement.querySelector('swiper-product-thumbs');
          if (galleryEl?.instance && thumbsEl?.instance) {
            // Gallery always initializes before thumbs (DOM order) after an AJAX swap,
            // so thumbs.swiper is undefined on the gallery. Re-connect them here.
            galleryEl.instance.thumbs.swiper = thumbsEl.instance;
            galleryEl.instance.thumbs.init();
            galleryEl.instance.update();
            thumbsEl.instance.update();
          } else {
            galleryEl?.instance?.update();
            thumbsEl?.instance?.update();
          }
        });
      }
    });
    return;
  }

  // --- Collection card ---
  const productCard = link.closest('product-card');
  if (productCard) {
    event.preventDefault();
    const newHref = link.getAttribute('href');

    // Update all product links to point to the selected color's product page
    productCard.querySelectorAll('[data-product-link]').forEach(el => el.setAttribute('href', newHref));

    // Update active swatch highlight
    link.closest('.product__product-groups')?.querySelectorAll('label').forEach(l => l.classList.remove('active'));
    link.closest('label')?.classList.add('active');

    // Swap the card image if the swatch contains a product image
    const swatchImg = link.querySelector('img');
    if (swatchImg) {
      const mainImg = productCard.querySelector('.product-card__media img:not(.hover-img)');
      if (mainImg) {
        mainImg.src = swatchImg.src;
        mainImg.srcset = swatchImg.srcset || '';
      }
    }
  }
});

} // end if (!customElements.get('product-card'))